import { UpdateBanner } from "@/components/UpdateBanner";
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, Wand, Film, Users, Mic, Youtube, Sparkles,
  BookOpen, Clapperboard, Palette, SlidersHorizontal, Wand2, Server,
  Upload, Settings, ListChecks, FlaskConical, Terminal, HardDrive, Cpu, Plus, ChevronDown, Film as FilmLogo
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import NewProjectDialog from "@/components/NewProjectDialog";
import { ImportHealthBanner } from "@/components/ingest/ImportHealthBanner";
import { fmtBytes } from "@/lib/api";

const PRIMARY = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/projects", label: "Projects", icon: FolderOpen },
  { to: "/wizard", label: "Edit Wizard", icon: Wand, needsProject: true },
  { to: "/media", label: "Media", icon: Film, needsProject: true },
  { to: "/people", label: "People", icon: Users, needsProject: true },
  { to: "/audio", label: "Audio", icon: Mic, phase: 2 },
  { to: "/references", label: "References", icon: Youtube, needsProject: true },
  { to: "/ai-analysis", label: "AI Analysis", icon: Sparkles, needsProject: true },
  { to: "/story", label: "Story", icon: BookOpen, needsProject: true },
  { to: "/timeline", label: "Timeline", icon: Clapperboard, needsProject: true },
  { to: "/color", label: "Color", icon: Palette, needsProject: true },
  { to: "/audio-mix", label: "Audio Mix", icon: SlidersHorizontal, needsProject: true },
  { to: "/enhancement", label: "Enhancement", icon: Wand2, needsProject: true },
  { to: "/render", label: "Render", icon: Server, needsProject: true },
  { to: "/export", label: "Export", icon: Upload, needsProject: true },
  { to: "/settings", label: "Settings", icon: Settings },
];

const SECONDARY = [
  { to: "/jobs", label: "Job Queue", icon: ListChecks, needsProject: true },
  { to: "/logs", label: "Logs", icon: Terminal, needsProject: true },
  { to: "/storage", label: "Storage", icon: HardDrive, needsProject: true },
  { to: "/beta", label: "Beta Report", icon: FlaskConical, needsProject: true },
];

function NavRow({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
    >
      <Icon size={18} strokeWidth={2} />
      <span className="flex-1">{item.label}</span>
      {item.phase && (
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#0c0e14] text-[#64748B] border border-[#252A3B]">
          P{item.phase}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { project, projects, selectProject, system } = useApp();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [switcher, setSwitcher] = useState(false);
  const navigate = useNavigate();

  const gpu = system?.gpu;
  const cpu = system?.cpu;

  return (
    <div className="flex h-screen overflow-hidden bg-[#090A0F]">
      <NewProjectDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />

      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#0c0e14] border-r border-[#252A3B] flex flex-col">
        <div className="h-14 flex items-center gap-2.5 px-5 border-b border-[#252A3B]">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E6B04E] to-[#b57e1d] flex items-center justify-center">
            <FilmLogo size={18} className="text-[#1a1405]" />
          </div>
          <div className="leading-tight">
            <div className="font-head font-bold text-[15px] text-[#F1F5F9]">AI Wedding Studio</div>
            <div className="metric-label" style={{ fontSize: 9 }}>Post-Production Suite</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {PRIMARY.map((it) => <NavRow key={it.to} item={it} />)}
          <div className="metric-label px-3 pt-4 pb-1">System</div>
          {SECONDARY.map((it) => <NavRow key={it.to} item={it} />)}
        </nav>

        <div className="p-3 border-t border-[#252A3B] space-y-2">
          <div className="flex items-center gap-2 text-[11px] font-mono text-[#64748B]">
            <Cpu size={13} className={gpu?.available ? "text-[#10B981]" : "text-[#64748B]"} />
            <span>{gpu?.available ? gpu.name : "CPU only"}</span>
          </div>
          {cpu && (
            <div className="flex items-center gap-2 text-[11px] font-mono text-[#64748B]">
              <HardDrive size={13} />
              <span>RAM {fmtBytes(cpu.ram_used)} / {fmtBytes(cpu.ram_total)}</span>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 shrink-0 border-b border-[#252A3B] bg-[#0c0e14]/80 flex items-center px-5 gap-4">
          <div className="relative">
            <button
              data-testid="project-switcher"
              onClick={() => setSwitcher((s) => !s)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[#212534] transition-colors"
            >
              <div className="text-left leading-tight">
                <div className="text-[13px] font-semibold text-[#F1F5F9]">
                  {project ? project.name : "No project selected"}
                </div>
                {project && (
                  <div className="metric-label" style={{ fontSize: 9 }}>
                    {project.bride_name} &amp; {project.groom_name}
                  </div>
                )}
              </div>
              <ChevronDown size={15} className="text-[#64748B]" />
            </button>
            {switcher && (
              <div className="absolute z-30 mt-2 w-72 glass rounded-xl p-2 shadow-2xl">
                {projects.length === 0 && (
                  <div className="px-3 py-4 text-sm text-[#64748B]">No projects yet</div>
                )}
                {projects.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`switch-project-${p.id}`}
                    onClick={() => { selectProject(p.id); setSwitcher(false); navigate("/"); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#212534] transition-colors"
                  >
                    <div className="text-[13px] font-medium text-[#F1F5F9]">{p.name}</div>
                    <div className="metric-label" style={{ fontSize: 9 }}>{p.clip_count} clips · {p.wedding_type}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-3 px-3 py-1.5 rounded-lg bg-[#12141C] border border-[#252A3B] font-mono text-[11px]">
              <span className={`flex items-center gap-1.5 ${system?.ffmpeg ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${system?.ffmpeg ? "bg-[#10B981]" : "bg-[#EF4444]"}`} />FFMPEG
              </span>
              <span className="text-[#252A3B]">|</span>
              <span className="text-[#94A3B8]">CPU {cpu?.percent ?? "--"}%</span>
              <span className="text-[#252A3B]">|</span>
              <span className={gpu?.available ? "text-[#00F0FF]" : "text-[#64748B]"}>
                {gpu?.available ? `GPU ${gpu.utilization}%` : "NO GPU"}
              </span>
            </div>
            <button data-testid="btn-create-new-project" onClick={() => setDialogOpen(true)} className="btn btn-gold">
              <Plus size={16} /> New Project
            </button>
          </div>
        </header>

        <ImportHealthBanner />
        <main className="flex-1 overflow-y-auto"><UpdateBanner />{children}</main>
      </div>
    </div>
  );
}
